import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { WeekDetail, WeekSummary } from '@/api/types'

/**
 * Review recorded time, a job-week at a time.
 *
 * Submission is per day, but a crew works a job for five or six days and the
 * supervisor reviews the whole stretch. Reading five separate day submissions
 * to check a week is exactly the job a grid does better — person down the
 * side, day across the top, hours in the cells, totals on both edges.
 *
 * Shared by both halves of the app: `mine` shows a supervisor only their own
 * weeks, while the office sees everything.
 */
export function ReviewPage({ mine = false }: { mine?: boolean }) {
  // Detail links must stay inside the shell the user is already in, or an
  // office user would land in the field chrome and vice versa.
  const basePath = mine ? '/field' : '/office'

  const [weeks, setWeeks] = useState<WeekSummary[] | null>(null)
  const [open, setOpen] = useState<WeekDetail | null>(null)
  const [loadingWeek, setLoadingWeek] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setWeeks(await api.weeks(mine))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load')
    }
  }, [mine])

  useEffect(() => {
    void load()
  }, [load])

  async function openWeek(w: WeekSummary) {
    setLoadingWeek(true)
    setError(null)
    try {
      setOpen(await api.week(w.job_no, w.week_start))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that week')
    } finally {
      setLoadingWeek(false)
    }
  }

  const fmtDay = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`)
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })
  }
  const fmtRange = (a: string, b: string) => {
    const s = new Date(`${a}T00:00:00`)
    const e = new Date(`${b}T00:00:00`)
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
    return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`
  }
  const hrs = (n: number) => (n ? Number(n).toFixed(2) : '')

  if (open) {
    return (
      <div className="stack">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="crew-heading">
            <h2 className="title" style={{ margin: 0 }}>
              <span className="mono">{open.job_no}</span> {open.job_description}
            </h2>
            <button className="btn btn-secondary" onClick={() => setOpen(null)}>
              ← All weeks
            </button>
          </div>
          <p className="muted">
            {fmtRange(open.week_start, open.week_end)} · {open.rows.length} crew ·{' '}
            <strong>{Number(open.total_hours).toFixed(2)}</strong> hours
            {Number(open.total_amount) > 0 && (
              <> · <strong>${Number(open.total_amount).toFixed(2)}</strong></>
            )}
          </p>
        </div>

        <div className="card">
          <h2>Hours by person and day</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Crew member</th>
                  {open.dates.map((d) => (
                    <th key={d} className="num">{fmtDay(d)}</th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {open.rows.map((r) => (
                  <tr key={r.sage_employee_key}>
                    <td>
                      {r.display_name}{' '}
                      <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                        {r.employee_no}
                      </span>
                    </td>
                    {r.cells.map((c) => (
                      <td key={c.date_worked} className="num">
                        {hrs(c.hours) || <span className="muted">·</span>}
                        {Number(c.amount) > 0 && (
                          <div className="muted" style={{ fontSize: '0.75rem' }}>
                            ${Number(c.amount).toFixed(2)}
                          </div>
                        )}
                      </td>
                    ))}
                    <td className="num">
                      <strong>{hrs(r.total_hours)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Day total</strong></td>
                  {open.day_totals.map((t, i) => (
                    <td key={i} className="num"><strong>{hrs(t)}</strong></td>
                  ))}
                  <td className="num">
                    <strong>{Number(open.total_hours).toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card">
          <h2>Days submitted</h2>
          <p className="muted">
            Open a day to correct a line. Anything already posted to Sage is
            locked — those corrections belong in Sage.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Submitted by</th>
                  <th className="num">Crew</th>
                  <th className="num">Lines</th>
                  <th className="num">Hours</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {open.submissions.map((s) => (
                  <tr key={s.submission_id}>
                    <td>{fmtDay(s.date_worked)}</td>
                    <td>{s.submitted_by ?? '—'}</td>
                    <td className="num">{s.crew_count}</td>
                    <td className="num">{s.line_count}</td>
                    <td className="num">{Number(s.total_hours).toFixed(2)}</td>
                    <td>
                      <span
                        className={`badge ${
                          s.status === 'PostedToSage'
                            ? 'badge-ok'
                            : s.status === 'PostFailed'
                              ? 'badge-error'
                              : 'badge-info'
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td>
                      <Link
                        className="btn btn-secondary"
                        style={{ minHeight: 32 }}
                        to={`${basePath}/submissions/${s.submission_id}`}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2>{mine ? 'My recorded time' : 'Recorded time'}</h2>
        <p className="muted">
          Grouped by job and week. Open one to see the whole stretch as a grid —
          every crew member down the side, each day across the top.
        </p>
      </div>

      {weeks === null ? (
        <div className="card"><p className="muted">Loading…</p></div>
      ) : weeks.length === 0 ? (
        <div className="card">
          <p className="muted">
            Nothing recorded yet. {mine ? 'Crew days you submit' : 'Submissions'}{' '}
            will appear here.
          </p>
        </div>
      ) : (
        weeks.map((w) => (
          <div className="card" key={`${w.job_no}-${w.week_start}`}>
            <div className="crew-heading">
              <h2 className="title" style={{ margin: 0 }}>
                <span className="mono">{w.job_no}</span> {w.job_description}{' '}
                {w.is_prevailing_wage && (
                  <span className="badge badge-warn">Prevailing wage</span>
                )}
                {!w.is_prevailing_wage && w.is_certified && (
                  <span className="badge badge-info">Certified</span>
                )}
              </h2>
              <span className="count-pill">{Number(w.total_hours).toFixed(0)} h</span>
            </div>
            <p className="muted">
              {fmtRange(w.week_start, w.week_end)} · {w.day_count}{' '}
              {w.day_count === 1 ? 'day' : 'days'} · {w.crew_count} crew ·{' '}
              {w.line_count} lines
              {w.submitted_by.length > 0 && <> · {w.submitted_by.join(', ')}</>}
            </p>
            <button
              className="btn btn-secondary"
              disabled={loadingWeek}
              onClick={() => void openWeek(w)}
            >
              Review the week
            </button>
          </div>
        ))
      )}
    </div>
  )
}
